import type { MetaGamesEventType } from '../../dto/metagames.types';
import type { SportEventType } from '../../dto/sport.types';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { JackpotCoinType, JackpotLaunchBlock } from '../jackpot/types';

export type GameProviderName = 'PRAGMATIC' | 'RUBY' | 'Barouletta Gaming' | 'Crownslots' | 'metagameProvider';

export type MasterCasinoProvider = {
  masterCasinoProviderId: number;
  name: string;
  isActive: boolean;
  thumbnailUrl: string | null;
  jackpotBadgeUrl: string | null;
  masterGameAggregatorId: number | null;
  uuid: string;
  createdAt: string;
  updatedAt: string;
};

type GameLabelStyle = {
  background: string;
  color: string;
};

type GameLabelType = 'manual' | 'tournament' | 'daily_mission' | 'royal_crowns' | 'slot_battle';

export type GameAudience = 'royal_crowns' | 'dynasty';

export type GameLabel = {
  id: string;
  name: string;
  type: GameLabelType;
  gcStyle?: GameLabelStyle;
  scStyle?: GameLabelStyle;
  iconSrc?: string;
  weight: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export interface GameCategoryConnection {
  gameSubcategoryId: number;
  masterCasinoGameId: number;
  masterGameSubCategoryId: number;
  orderId: number;
}

/**
 * One enabled Crown Jackpot group a game contributes to.
 *
 * `coinType` is the JACKPOT coin (`CC` | `SC`), not the platform coin (`GC` | `SC`) — it comes straight
 * off `splash_jackpot.jackpot.coin_type`. It has to be the jackpot vocabulary, because the badge's CSS
 * matches this value against `<body data-jp-coin>`, which is `toJackpotCoin(platformCoin)`. Mixing the
 * two spellings means every gold-coin badge silently never shows.
 */
export interface GameJackpot {
  jackpotId: string;
  coinType: JackpotCoinType;
}

export interface Game {
  masterCasinoGameId: number;
  name: string;
  masterCasinoProviderId: number;
  identifier: string;
  thumbnailUrl: string | null;
  thumbnailHrUrl: string | null;
  thumbnailShortUrl: string | null;
  thumbnailShortHrUrl: string | null;
  thumbnailLongUrl: string | null;
  thumbnailLongHrUrl: string | null;
  masterGameSubCategoryId: number;
  masterCasinoCategoryId: number;
  masterCasinoCategoryName: string;
  moreDetails?: any;
  MasterCasinoProvider?: MasterCasinoProvider;
  masterCasinoProvider?: MasterCasinoProvider;
  gameLabels: GameLabel[];
  gameCategoryConnections?: GameCategoryConnection[];
  /** Set by player-backend: the game is visible only because `can_test_casino_games` is on. */
  isTestingOnly?: boolean;
  /** Set by player-backend for testers only: the groups an "only for" game is limited to. */
  testerAudiences?: GameAudience[];
  /**
   * Crown Jackpot groups this game contributes to (KRA-303), set by player-backend from a LEFT JOIN on
   * `splash_jackpot.jackpot_brand_game_participation` when the category / search rows are built.
   *
   * Enabled, non-draft groups only. Absent or empty means the tile carries no jackpot badge.
   *
   * WHY THE COIN IS HERE and not derived on this side: the games listing is not coin-scoped — a tile is
   * playable in both GC and SC — and this app only holds eligibility for the coin currently selected, so
   * it cannot map a group id to its coin on its own. The coin is also what the badge's CSS matches on;
   * see `JackpotBadgeStyles` / `data-jackpot-coins`.
   */
  jackpots?: GameJackpot[];
}

export interface GamesResponse {
  games: Game[];
  hasMoreGames: boolean;
}

export interface LaunchGameData {
  gameUrl: string;
  slotGameUrl: string;
  isGameUi: boolean;
  gameId: string;
  gameName: string;
  identifier: string;
  providerName: GameProviderName;
  providerId: number;
  crownRips?: {
    token: string; // launch session token — Crown Rips builds its own game-ui URL, reads the session here
  };
  sportsEvents?: {
    token: string;
    path: string;
    type: SportEventType;
  };
  metagames?: {
    token: string;
    path: string;
    type: MetaGamesEventType;
    accessToken?: string;
  };
  /**
   * Crown Jackpot in-slot banner context (KRA-303). Present only when the launch qualifies — see
   * {@link JackpotLaunchBlock}. Forwarded into the game-ui iframe by GameStore, not rendered here.
   */
  jackpot?: JackpotLaunchBlock;
  /** True only when admin config + LD flag allow playing-now presence pings for this game (PLT-789). */
  playingNow?: boolean;
}

export type LaunchGameRequest = {
  gameId: string | number;
  type?: string;
  coin: CoinType;
};

export type LaunchGameResponse = {
  error: number;
  description: string;
  gameUrl: string;
  slotGameUrl: string;
  success: boolean;
};

export enum GameProvider {
  Pragmatic = 'Pragmatic Play',
  Playson = 'Playson',
  PlaysonInfin = 'Playson  infin',
  Crownslots = 'Crownslots',
}

export interface SwipeHelperVisibleMessage {
  type: 'setSwipeHelperVisible';
  payload: { visible: boolean };
}
